import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { safelyOpenURL } from 'app/lib/navigation';
import { useUser, useUserIsInsured } from 'app/network/user';
import type { NavigationScreen } from 'app/types/navigation';
import React, { useEffect } from 'react';
import { Pressable } from 'react-native';

import { IconArrowUpRight } from '@tabler/icons-react-native';
import { useNavigationOptions } from 'app/hooks/navigation';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { getI18nLanguage } from 'app/lib/language';
import { preparePolicyUrl } from 'app/lib/text';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { usePageTitle } from 'app/utils/web-helpers';

const PrivacyScreenComponent: NavigationScreen<'privacy'> = ({ navigation }) => {
  const { t } = useScopedTranslation();
  const i18nLanguage = getI18nLanguage();

  const { data: user } = useUser();
  const isInsured = useUserIsInsured();

  const setPageTitle = usePageTitle('privacy');

  useEffect(() => setPageTitle('title'), [setPageTitle]);

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(`title`)}
        </Text>
      ),
    },
    false,
  );

  return (
    <Page title={t('title')}>
      <Column
        gap="spacing.general.x4"
        padding="spacing.general.x4"
        backgroundColor="surface.tertiary-neutral"
        borderRadius="radius.lg"
        paddingHorizontal="spacing.general.x4"
      >
        <Pressable onPress={() => void safelyOpenURL(preparePolicyUrl(t('link1'), i18nLanguage, user))}>
          <Row alignItems="center">
            <Text flex={1} fontSize={18}>
              {t('button1Text')}
            </Text>
            <IconArrowUpRight strokeWidth={ICON_STROKE_WIDTH} />
          </Row>
        </Pressable>

        <Pressable onPress={() => void safelyOpenURL(preparePolicyUrl(t('link2'), i18nLanguage, user))}>
          <Row alignItems="center">
            <Text flex={1} fontSize={18}>
              {t('button2Text')}
            </Text>
            <IconArrowUpRight strokeWidth={ICON_STROKE_WIDTH} />
          </Row>
        </Pressable>

        {isInsured ? (
          <Pressable onPress={() => void safelyOpenURL(preparePolicyUrl(t('link3'), i18nLanguage, user))}>
            <Row alignItems="center">
              <Text flex={1} fontSize={18}>
                {t('button3Text')}
              </Text>
              <IconArrowUpRight strokeWidth={ICON_STROKE_WIDTH} />
            </Row>
          </Pressable>
        ) : null}
      </Column>
    </Page>
  );
};

export const PrivacyScreen: NavigationScreen<'privacy'> = withErrorBoundary(
  withDashboardLayout(PrivacyScreenComponent),
);
